import { useState, useEffect } from "react";
import api from "./api";

/*
 ╔══════════════════════════════════════════════════════════╗
 ║  CHOPTIME AI — Landing.js  v2.0                         ║
 ║  NEW: How It Works · Demo Video · Waitlist Form         ║
 ║       Investor Section · Google Analytics               ║
 ║                                                          ║
 ║  In loving memory of Mina Gbemisola LAAK                ║
 ║  MGL2390 · Born October 23, 1990                        ║
 ╚══════════════════════════════════════════════════════════╝
*/

/* ── GOOGLE ANALYTICS ─────────────────────────────────────
   Replace GA_ID with your real G-XXXXXXXXXX from
   analytics.google.com → Admin → Data Streams
──────────────────────────────────────────────────────────── */
const GA_ID = "G-XXXXXXXXXX"; // ← replace with your real ID

function initGA() {
  if (typeof window === "undefined" || window.GA_INITIALIZED) return;
  window.GA_INITIALIZED = true;
  const s = document.createElement("script");
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  s.async = true;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID);
}

function trackEvent(action, category, label) {
  if (window.gtag)
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
    });
}

/* ── FORMSPREE ────────────────────────────────────────────
   1. Go to formspree.io → create free account → new form
   2. Copy your endpoint: https://formspree.io/f/xxxxxxxz
   3. Replace below. Until then, form runs in demo mode.
──────────────────────────────────────────────────────────── */
const FORMSPREE_URL = "https://formspree.io/f/YOUR_FORM_ID"; // ← replace

/* ── YOUTUBE VIDEO ID ─────────────────────────────────────
   From your video URL: youtube.com/watch?v=VIDEO_ID_HERE
   Replace below once you upload your 60-sec demo video.
──────────────────────────────────────────────────────────── */
const VIDEO_ID = "YOUR_VIDEO_ID"; // ← replace

/* ─── STYLES ─────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800;900&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');

/* ── RESET & TOKENS ── */
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg0:#04080a; --bg1:#081210; --bg2:#0d1c18;
  --bg3:#142420; --bg4:#1a2e28;
  --teal:#00d4a0; --teal2:#009e78; --teal3:#00ffbe;
  --teal-dim:rgba(0,212,160,.1); --teal-glow:rgba(0,212,160,.25);
  --amber:#ffab2e; --red:#ff5252; --blue:#4db8ff;
  --text:#deeee8; --text2:#6e9e8c; --text3:#2e4e44;
  --border:rgba(255,255,255,.06); --border2:rgba(0,212,160,.25);
  --r-sm:10px; --r-md:16px; --r-lg:22px; --r-xl:32px;
  --glow:0 0 40px rgba(0,212,160,.18);
}
html{scroll-behavior:smooth}
html,body{min-height:100%;background:var(--bg0);overflow-x:hidden}
body{font-family:'DM Sans',sans-serif;color:var(--text);-webkit-font-smoothing:antialiased}
::-webkit-scrollbar{width:3px}
::-webkit-scrollbar-thumb{background:var(--bg3);border-radius:4px}

/* ── ANIMATIONS ── */
@keyframes fadeUp{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes blink{0%,100%{opacity:1}50%{opacity:.25}}
@keyframes gradShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
@keyframes heartbeat{0%,100%{transform:scale(1)}14%{transform:scale(1.18)}28%{transform:scale(1)}42%{transform:scale(1.1)}70%{transform:scale(1)}}
@keyframes wv{0%,100%{height:4px}50%{height:20px}}
@keyframes glowPulse{0%,100%{box-shadow:0 0 20px rgba(0,212,160,.15)}50%{box-shadow:0 0 50px rgba(0,212,160,.35)}}
@keyframes candleFlicker{0%,100%{opacity:1;transform:scale(1)}33%{opacity:.85;transform:scale(1.05) rotate(-2deg)}66%{opacity:.92;transform:scale(.97) rotate(1deg)}}

.l-au1{animation:fadeUp .7s ease both}
.l-au2{animation:fadeUp .7s .12s ease both}
.l-au3{animation:fadeUp .7s .22s ease both}
.l-au4{animation:fadeUp .7s .34s ease both}
.l-au5{animation:fadeUp .7s .46s ease both}
.l-au6{animation:fadeUp .7s .58s ease both}

/* ── SHARED BUTTONS ── */
.l-btn{display:inline-flex;align-items:center;gap:7px;font-family:'DM Sans',sans-serif;
  font-size:13px;font-weight:600;padding:9px 20px;border-radius:var(--r-sm);
  cursor:pointer;border:none;transition:all .18s;white-space:nowrap}
.l-btn-ghost{background:transparent;color:var(--text2);border:1px solid var(--border)}
.l-btn-ghost:hover{color:var(--teal);border-color:var(--border2)}
.l-btn-teal{background:var(--teal);color:#04080a;font-weight:700}
.l-btn-teal:hover{background:var(--teal3);transform:translateY(-1px);box-shadow:0 6px 24px var(--teal-glow)}
.l-btn-lg{font-size:15px;padding:14px 32px;border-radius:var(--r-md)}
.l-btn-xl{font-size:16px;padding:16px 40px;border-radius:var(--r-md);font-weight:800}

/* ── NAVBAR ── */
.l-nav{position:fixed;top:0;left:0;right:0;z-index:200;
  display:flex;align-items:center;justify-content:space-between;
  padding:0 48px;height:64px;
  background:rgba(4,8,10,.88);backdrop-filter:blur(20px);
  border-bottom:1px solid var(--border);transition:all .3s}
.l-nav.scrolled{background:rgba(4,8,10,.98);border-bottom-color:rgba(0,212,160,.15)}
.l-nav-logo{display:flex;align-items:center;gap:10px;cursor:pointer}
.l-nav-logo-icon{width:34px;height:34px;border-radius:9px;
  background:linear-gradient(135deg,var(--teal),var(--teal2));
  display:flex;align-items:center;justify-content:center;font-size:17px;
  box-shadow:0 0 14px var(--teal-glow)}
.l-nav-logo-name{font-family:'Syne',sans-serif;font-weight:900;font-size:15px}
.l-nav-logo-name em{color:var(--teal);font-style:normal}
.l-nav-links{display:flex;align-items:center;gap:28px}
.l-nav-link{font-size:13px;font-weight:500;color:var(--text2);cursor:pointer;transition:color .15s}
.l-nav-link:hover{color:var(--teal)}
.l-nav-mgl{font-size:10px;font-weight:700;color:rgba(0,212,160,.4);letter-spacing:1px}
.l-nav-btns{display:flex;gap:10px}

/* ── HERO ── */
.l-hero{min-height:100vh;display:flex;align-items:center;
  padding:100px 48px 80px;position:relative;overflow:hidden}
.l-hero-mesh{position:absolute;inset:0;pointer-events:none;
  background:radial-gradient(ellipse 65% 55% at 65% 45%,rgba(0,212,160,.07) 0%,transparent 60%),
             radial-gradient(ellipse 40% 30% at 5% 85%,rgba(0,212,160,.04) 0%,transparent 50%),
             radial-gradient(ellipse 35% 25% at 92% 8%,rgba(255,171,46,.04) 0%,transparent 50%)}
.l-hero-grid{position:absolute;inset:0;pointer-events:none;
  background-image:linear-gradient(rgba(0,212,160,.035) 1px,transparent 1px),
                   linear-gradient(90deg,rgba(0,212,160,.035) 1px,transparent 1px);
  background-size:64px 64px;
  mask-image:radial-gradient(ellipse 85% 85% at 50% 50%,black 20%,transparent 75%)}
.l-hero-inner{max-width:1100px;margin:0 auto;width:100%;
  display:grid;grid-template-columns:1fr 420px;gap:60px;
  align-items:center;position:relative;z-index:1}
.l-hero-badge{display:inline-flex;align-items:center;gap:8px;
  background:rgba(0,212,160,.08);border:1px solid rgba(0,212,160,.2);
  border-radius:20px;padding:6px 14px;font-size:12px;font-weight:700;
  color:var(--teal);margin-bottom:24px;letter-spacing:.2px}
.l-hero-badge-dot{width:6px;height:6px;border-radius:50%;background:var(--teal);animation:blink 1.4s infinite}
.l-hero-title{font-family:'Syne',sans-serif;font-size:clamp(36px,4.5vw,64px);
  font-weight:900;line-height:1.06;letter-spacing:-1.5px;margin-bottom:8px}
.l-hero-grad{background:linear-gradient(135deg,var(--teal) 0%,var(--teal3) 40%,var(--amber) 100%);
  background-size:200% 200%;-webkit-background-clip:text;
  -webkit-text-fill-color:transparent;background-clip:text;animation:gradShift 4s ease infinite}
.l-hero-sub{font-size:clamp(14px,1.8vw,18px);color:var(--text2);line-height:1.75;
  margin:18px 0 32px;max-width:540px}
.l-hero-sub strong{color:var(--text);font-weight:600}
.l-hero-actions{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:44px}
.l-hero-stats{display:flex;gap:32px;flex-wrap:wrap}
.l-hs-val{font-family:'Syne',sans-serif;font-size:24px;font-weight:900;line-height:1}
.l-hs-lbl{font-size:11px;color:var(--text2);margin-top:3px;font-weight:500}

/* phone mockup */
.l-phone-wrap{animation:float 5s ease-in-out infinite;position:relative}
.l-phone-wrap::before{content:'';position:absolute;bottom:-36px;left:50%;transform:translateX(-50%);
  width:160px;height:36px;border-radius:50%;
  background:radial-gradient(ellipse,rgba(0,212,160,.2),transparent 70%);filter:blur(10px)}
.l-phone{width:240px;height:490px;margin:0 auto;background:var(--bg1);
  border:2px solid rgba(0,212,160,.28);border-radius:38px;
  position:relative;overflow:hidden;
  box-shadow:0 40px 100px rgba(0,0,0,.65),0 0 60px rgba(0,212,160,.12)}
.l-phone-notch{width:72px;height:22px;background:var(--bg0);border-radius:0 0 14px 14px;margin:0 auto}
.l-phone-body{padding:8px 12px;height:calc(100% - 22px);display:flex;flex-direction:column;gap:6px;overflow:hidden}
.l-phone-status{display:flex;justify-content:space-between;font-size:8px;color:var(--text3);margin-bottom:2px}
.l-ph-ring{display:flex;justify-content:center;align-items:center;position:relative;padding:4px 0}
.l-ph-ring-center{position:absolute;text-align:center}
.l-ph-ring-val{font-family:'Syne',sans-serif;font-size:18px;font-weight:900;line-height:1}
.l-ph-ring-lbl{font-size:8px;color:var(--teal);font-weight:700}
.l-ph-card{background:var(--bg2);border:1px solid var(--border);border-radius:9px;padding:7px 9px}
.l-ph-card-t{font-size:8px;font-weight:700;margin-bottom:2px}
.l-ph-card-b{font-size:7px;color:var(--text2);line-height:1.4}
.l-ph-ok{background:rgba(0,212,160,.08);border:1px solid rgba(0,212,160,.2);border-radius:9px;padding:7px 9px;display:flex;gap:5px}
.l-ph-warn{background:rgba(255,171,46,.08);border:1px solid rgba(255,171,46,.2);border-radius:9px;padding:7px 9px;display:flex;gap:5px}
.l-ph-icon{font-size:11px;flex-shrink:0}
.l-ph-txt{font-size:7px;color:var(--text2);line-height:1.4}
.l-ph-bottom{margin-top:auto;background:var(--bg2);border-top:1px solid var(--border);
  display:flex;padding:5px 0 8px;border-radius:0 0 30px 30px}
.l-ph-nav{flex:1;display:flex;flex-direction:column;align-items:center;gap:2px}
.l-ph-dot{width:12px;height:12px;border-radius:3px;background:var(--bg4)}
.l-ph-dot.on{background:var(--teal)}
.l-ph-lbl{font-size:5px;color:var(--text3)}.l-ph-lbl.on{color:var(--teal)}

/* ── MEMORIAL ── */
.l-memorial{padding:90px 48px;background:linear-gradient(180deg,var(--bg0),#04120d);position:relative;overflow:hidden}
.l-memorial::before{content:'';position:absolute;inset:0;
  background:radial-gradient(ellipse 60% 50% at 50% 50%,rgba(0,212,160,.05) 0%,transparent 60%);pointer-events:none}
.l-mem-inner{max-width:960px;margin:0 auto;display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:center}
.l-mem-card{background:linear-gradient(145deg,#04120d,#071a13);border:1px solid rgba(0,212,160,.22);
  border-radius:var(--r-xl);padding:36px;text-align:center;position:relative;overflow:hidden;
  animation:glowPulse 4s ease-in-out infinite}
.l-mem-card::before{content:'';position:absolute;top:-50px;left:50%;transform:translateX(-50%);
  width:220px;height:220px;border-radius:50%;
  background:radial-gradient(circle,rgba(0,212,160,.1),transparent 70%);pointer-events:none}
.l-candle{font-size:48px;display:block;margin-bottom:6px;animation:candleFlicker 3s ease-in-out infinite}
.l-mem-code{font-family:'Syne',sans-serif;font-size:11px;font-weight:700;color:var(--teal);
  letter-spacing:3px;text-transform:uppercase;margin-bottom:4px;opacity:.7}
.l-mem-name{font-family:'Syne',sans-serif;font-size:24px;font-weight:900;margin-bottom:4px;line-height:1.2}
.l-mem-dates{font-size:13px;color:var(--teal);font-weight:600;margin-bottom:18px}
.l-mem-divider{height:1px;background:linear-gradient(90deg,transparent,rgba(0,212,160,.3),transparent);margin:14px 0}
.l-mem-text{font-size:13px;color:var(--text2);line-height:1.75;margin-bottom:12px;font-style:italic}
.l-mem-badge{display:inline-flex;align-items:center;gap:6px;
  background:rgba(0,212,160,.08);border:1px solid rgba(0,212,160,.2);
  border-radius:20px;padding:5px 14px;font-size:11px;font-weight:700;color:var(--teal);letter-spacing:1px}
.l-story-label{font-size:11px;font-weight:700;color:var(--teal);text-transform:uppercase;letter-spacing:1.5px;margin-bottom:14px}
.l-story-title{font-family:'Syne',sans-serif;font-size:clamp(22px,3vw,36px);font-weight:900;
  line-height:1.15;letter-spacing:-.4px;margin-bottom:16px}
.l-story-title em{color:var(--teal);font-style:normal}
.l-story-body{font-size:15px;color:var(--text2);line-height:1.85;margin-bottom:12px}
.l-story-body strong{color:var(--text);font-weight:600}
.l-story-quote{border-left:3px solid var(--teal);padding:14px 18px;
  background:rgba(0,212,160,.05);border-radius:0 12px 12px 0;
  font-size:14px;font-style:italic;color:var(--text);line-height:1.7;margin:18px 0}
.l-story-quote cite{display:block;font-size:11px;color:var(--teal);font-style:normal;font-weight:700;margin-top:8px}

/* ── GENERIC SECTION ── */
.l-section{padding:90px 48px}
.l-sec-inner{max-width:1000px;margin:0 auto}
.l-sec-label{font-size:11px;font-weight:700;color:var(--teal);text-transform:uppercase;letter-spacing:1.5px;margin-bottom:12px;text-align:center}
.l-sec-title{font-family:'Syne',sans-serif;font-size:clamp(22px,3vw,38px);font-weight:900;text-align:center;letter-spacing:-.4px;margin-bottom:12px;line-height:1.2}
.l-sec-sub{font-size:15px;color:var(--text2);text-align:center;max-width:540px;margin:0 auto 44px;line-height:1.7}

/* problem cards */
.l-pgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.l-pcard{background:var(--bg2);border:1px solid var(--border);border-radius:var(--r-lg);
  padding:24px;position:relative;overflow:hidden;transition:all .2s}
.l-pcard:hover{border-color:var(--border2);transform:translateY(-3px)}
.l-pcard::after{content:'';position:absolute;top:0;left:0;right:0;height:3px;border-radius:3px 3px 0 0}
.l-pcard.r::after{background:var(--red)}.l-pcard.a::after{background:var(--amber)}.l-pcard.t::after{background:var(--teal)}
.l-pcard-icon{font-size:28px;margin-bottom:12px;display:block}
.l-pcard-title{font-family:'Syne',sans-serif;font-size:15px;font-weight:800;margin-bottom:7px}
.l-pcard-body{font-size:13px;color:var(--text2);line-height:1.65}
.l-pcard-num{font-family:'Syne',sans-serif;font-size:30px;font-weight:900;margin-top:12px;line-height:1}
.l-pcard-num.r{color:var(--red)}.l-pcard-num.a{color:var(--amber)}.l-pcard-num.t{color:var(--teal)}

/* feature cards */
.l-fgrid{display:grid;grid-template-columns:repeat(2,1fr);gap:14px}
.l-fcard{background:var(--bg2);border:1px solid var(--border);border-radius:var(--r-lg);
  padding:22px;display:flex;gap:14px;align-items:flex-start;transition:all .2s}
.l-fcard:hover{border-color:var(--border2);transform:translateX(3px)}
.l-fcard-icon{width:46px;height:46px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0}
.l-fcard-title{font-family:'Syne',sans-serif;font-size:14px;font-weight:800;margin-bottom:5px}
.l-fcard-body{font-size:13px;color:var(--text2);line-height:1.6}

/* country cards */
.l-cgrid{display:flex;justify-content:center;gap:14px;flex-wrap:wrap}
.l-ccard{background:var(--bg2);border:1px solid var(--border);border-radius:var(--r-lg);
  padding:20px 22px;text-align:center;transition:all .2s;min-width:130px;cursor:default}
.l-ccard:hover{border-color:var(--border2);transform:translateY(-4px);box-shadow:0 14px 40px rgba(0,0,0,.3)}
.l-ccard-flag{font-size:34px;margin-bottom:7px;display:block}
.l-ccard-name{font-family:'Syne',sans-serif;font-size:13px;font-weight:800;margin-bottom:2px}
.l-ccard-users{font-size:11px;color:var(--teal);font-weight:600}

/* metrics */
.l-metrics{padding:70px 48px;background:linear-gradient(135deg,#04120d,#061a10);
  border-top:1px solid rgba(0,212,160,.12);border-bottom:1px solid rgba(0,212,160,.12)}
.l-mrow{display:flex;justify-content:center;max-width:800px;margin:0 auto;flex-wrap:wrap}
.l-metric{flex:1;min-width:150px;text-align:center;padding:20px;border-right:1px solid rgba(0,212,160,.1)}
.l-metric:last-child{border:none}
.l-metric-val{font-family:'Syne',sans-serif;font-size:40px;font-weight:900;color:var(--teal);line-height:1}
.l-metric-lbl{font-size:12px;color:var(--text2);margin-top:5px;font-weight:500}

/* testimonials */
.l-tgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.l-tcard{background:var(--bg2);border:1px solid var(--border);border-radius:var(--r-lg);padding:22px;transition:border-color .2s}
.l-tcard:hover{border-color:var(--border2)}
.l-tcard-q{font-size:13px;color:var(--text2);line-height:1.75;margin-bottom:14px;font-style:italic}
.l-tcard-q::before{content:'"';font-size:28px;color:var(--teal);font-family:'Syne',sans-serif;font-weight:900;line-height:.4;display:block;margin-bottom:8px}
.l-tcard-av{width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Syne',sans-serif;font-weight:900;font-size:12px;flex-shrink:0}

/* CTA */
.l-cta{padding:100px 48px;text-align:center;background:linear-gradient(180deg,var(--bg0),#04120d);position:relative;overflow:hidden}
.l-cta-glow{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
  width:500px;height:500px;border-radius:50%;
  background:radial-gradient(circle,rgba(0,212,160,.07),transparent 70%);pointer-events:none}
.l-cta-title{font-family:'Syne',sans-serif;font-size:clamp(28px,4vw,50px);font-weight:900;letter-spacing:-.5px;margin-bottom:14px;line-height:1.1}
.l-cta-sub{font-size:16px;color:var(--text2);max-width:480px;margin:0 auto 32px;line-height:1.7}
.l-cta-mgl{font-size:11px;color:rgba(0,212,160,.35);font-weight:700;letter-spacing:2px;margin-top:28px}

/* footer */
.l-footer{padding:32px 48px;border-top:1px solid var(--border);background:var(--bg0);
  display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:14px}
.l-footer-copy{font-size:12px;color:var(--text3);line-height:1.6}
.l-footer-heart{color:var(--red);animation:heartbeat 2.5s ease-in-out infinite;display:inline-block}
.l-footer-mgl{font-size:10px;color:rgba(0,212,160,.35);font-weight:700;letter-spacing:1.5px;margin-top:2px}
.l-footer-links{display:flex;gap:20px;flex-wrap:wrap}
.l-footer-link{font-size:12px;color:var(--text3);cursor:pointer;transition:color .15s}
.l-footer-link:hover{color:var(--teal)}

/* waveform */
.l-wave{display:flex;align-items:center;gap:3px;height:20px}
.l-wave span{display:block;width:3px;background:var(--teal);border-radius:2px;animation:wv 1s ease-in-out infinite}
.l-wave span:nth-child(1){animation-delay:0s}.l-wave span:nth-child(2){animation-delay:.1s}
.l-wave span:nth-child(3){animation-delay:.2s}.l-wave span:nth-child(4){animation-delay:.3s}
.l-wave span:nth-child(5){animation-delay:.15s}

/* ── AUTH MODAL ── */
.l-auth-overlay{position:fixed;inset:0;background:rgba(0,0,0,.82);
  backdrop-filter:blur(16px);z-index:999;
  display:flex;align-items:center;justify-content:center;
  animation:fadeIn .2s ease;padding:16px}
.l-auth-modal{width:100%;max-width:880px;background:var(--bg1);
  border:1px solid var(--border);border-radius:var(--r-xl);overflow:hidden;
  box-shadow:0 40px 120px rgba(0,0,0,.75),0 0 60px rgba(0,212,160,.08);
  display:grid;grid-template-columns:1fr 1fr;
  max-height:96vh;animation:fadeUp .3s ease;position:relative}
.l-auth-close{position:absolute;top:16px;right:16px;width:30px;height:30px;
  border-radius:50%;background:var(--bg3);border:1px solid var(--border);
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;font-size:15px;color:var(--text2);transition:all .15s;z-index:10}
.l-auth-close:hover{background:var(--bg4);color:var(--text);border-color:var(--border2)}

/* auth left panel */
.l-auth-left{background:linear-gradient(150deg,#04120d,#071a13,#041510);
  padding:44px 36px;display:flex;flex-direction:column;
  justify-content:space-between;position:relative;overflow:hidden}
.l-auth-left::before{content:'';position:absolute;top:-60px;right:-60px;width:220px;height:220px;border-radius:50%;background:radial-gradient(circle,rgba(0,212,160,.12),transparent 70%);pointer-events:none}
.l-auth-left::after{content:'';position:absolute;bottom:-50px;left:-50px;width:180px;height:180px;border-radius:50%;background:radial-gradient(circle,rgba(255,171,46,.06),transparent 70%);pointer-events:none}
.l-auth-logo-row{display:flex;align-items:center;gap:10px;margin-bottom:32px;position:relative;z-index:1}
.l-auth-logo-icon{width:38px;height:38px;border-radius:10px;background:linear-gradient(135deg,var(--teal),var(--teal2));display:flex;align-items:center;justify-content:center;font-size:19px;box-shadow:0 0 18px var(--teal-glow)}
.l-auth-logo-name{font-family:'Syne',sans-serif;font-weight:900;font-size:17px}
.l-auth-logo-name em{color:var(--teal);font-style:normal}
.l-auth-tagline{font-family:'Syne',sans-serif;font-size:24px;font-weight:900;line-height:1.25;margin-bottom:12px;letter-spacing:-.3px;position:relative;z-index:1}
.l-auth-tagline em{color:var(--teal);font-style:normal}
.l-auth-desc{font-size:13px;color:var(--text2);line-height:1.7;margin-bottom:22px;position:relative;z-index:1}
.l-auth-feats{display:flex;flex-direction:column;gap:9px;margin-bottom:24px;position:relative;z-index:1}
.l-auth-feat{display:flex;align-items:center;gap:9px;font-size:12px;color:var(--text2)}
.l-auth-feat-icon{width:26px;height:26px;border-radius:7px;background:rgba(0,212,160,.1);border:1px solid rgba(0,212,160,.2);display:flex;align-items:center;justify-content:center;font-size:12px;flex-shrink:0}
.l-auth-memorial{background:rgba(0,212,160,.05);border:1px solid rgba(0,212,160,.12);border-radius:var(--r-md);padding:12px 14px;position:relative;z-index:1;display:flex;gap:10px;align-items:flex-start}
.l-auth-candle{font-size:18px;animation:candleFlicker 3s ease-in-out infinite;flex-shrink:0;margin-top:2px}
.l-auth-mem-text{font-size:11px;color:var(--text2);line-height:1.6;font-style:italic}
.l-auth-mem-text strong{color:var(--teal);font-style:normal}
.l-auth-mem-code{font-size:10px;color:rgba(0,212,160,.5);font-weight:700;letter-spacing:1px;margin-top:4px}

/* auth right panel */
.l-auth-right{padding:44px 36px;display:flex;flex-direction:column;justify-content:center;overflow-y:auto}
.l-auth-tabs{display:flex;margin-bottom:28px;background:var(--bg2);border-radius:var(--r-sm);padding:3px;border:1px solid var(--border)}
.l-auth-tab{flex:1;text-align:center;padding:9px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;color:var(--text2);transition:all .15s}
.l-auth-tab.active{background:var(--teal);color:var(--bg0)}
.l-auth-form-title{font-family:'Syne',sans-serif;font-size:21px;font-weight:900;margin-bottom:5px;letter-spacing:-.2px}
.l-auth-form-sub{font-size:13px;color:var(--text2);margin-bottom:22px;line-height:1.5}
.l-fg{margin-bottom:13px}
.l-fl{font-size:10px;font-weight:600;color:var(--text2);letter-spacing:.4px;text-transform:uppercase;margin-bottom:5px}
.l-fi{width:100%;background:var(--bg2);border:1.5px solid var(--border);border-radius:var(--r-sm);
  padding:11px 13px;font-family:'DM Sans',sans-serif;font-size:14px;color:var(--text);
  outline:none;transition:border-color .15s,box-shadow .15s}
.l-fi:focus{border-color:var(--teal);box-shadow:0 0 0 3px rgba(0,212,160,.1)}
.l-fi::placeholder{color:var(--text3)}
select.l-fi{cursor:pointer}
option{background:var(--bg2)}
.l-fi-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:13px}
.l-fi-wrap{position:relative}
.l-fi-wrap .l-fi{padding-right:40px}
.l-fi-eye{position:absolute;right:11px;top:50%;transform:translateY(-50%);cursor:pointer;color:var(--text3);font-size:15px;transition:color .15s;user-select:none}
.l-fi-eye:hover{color:var(--teal)}
.l-pw-bar{height:3px;background:var(--bg3);border-radius:2px;margin-top:5px;overflow:hidden}
.l-pw-fill{height:100%;border-radius:2px;transition:width .3s,background .3s}
.l-auth-submit{width:100%;padding:13px;border-radius:var(--r-sm);
  background:var(--teal);color:var(--bg0);font-family:'Syne',sans-serif;
  font-size:15px;font-weight:800;cursor:pointer;border:none;transition:all .18s;
  margin-top:6px;display:flex;align-items:center;justify-content:center;gap:8px}
.l-auth-submit:hover{background:var(--teal3);transform:translateY(-1px);box-shadow:0 8px 28px var(--teal-glow)}
.l-auth-submit:disabled{opacity:.6;cursor:not-allowed;transform:none}
.l-auth-divider{display:flex;align-items:center;gap:10px;margin:14px 0}
.l-auth-div-line{flex:1;height:1px;background:var(--border)}
.l-auth-div-txt{font-size:11px;color:var(--text3)}
.l-soc-row{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-bottom:14px}
.l-soc-btn{display:flex;align-items:center;justify-content:center;gap:7px;
  padding:10px;border-radius:var(--r-sm);background:var(--bg2);
  border:1.5px solid var(--border);font-size:13px;font-weight:600;
  color:var(--text2);cursor:pointer;transition:all .15s}
.l-soc-btn:hover{border-color:var(--border2);color:var(--text);transform:translateY(-1px)}
.l-auth-terms{font-size:10px;color:var(--text3);text-align:center;line-height:1.65;margin-top:12px}
.l-auth-terms a{color:var(--teal);text-decoration:none;cursor:pointer}
.l-spinner{width:16px;height:16px;border:2px solid rgba(4,8,10,.25);border-top-color:var(--bg0);border-radius:50%;animation:spin .6s linear infinite}
.l-success{text-align:center;padding:20px 0;animation:fadeUp .4s ease}
.l-success-icon{font-size:48px;display:block;margin-bottom:14px;animation:heartbeat 1.5s ease infinite}
.l-success-title{font-family:'Syne',sans-serif;font-size:20px;font-weight:900;margin-bottom:8px}
.l-success-sub{font-size:13px;color:var(--text2);line-height:1.6;margin-bottom:20px}
.l-success-btn{background:var(--teal);color:var(--bg0);border:none;border-radius:var(--r-sm);
  padding:12px 28px;font-family:'Syne',sans-serif;font-size:14px;font-weight:800;
  cursor:pointer;transition:all .18s;animation:fadeUp .4s .3s ease both}
.l-success-btn:hover{background:var(--teal3);box-shadow:0 6px 20px var(--teal-glow)}

/* ── HOW IT WORKS ── */
.l-how-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:0;position:relative;max-width:900px;margin:0 auto}
.l-how-grid::before{content:'';position:absolute;top:36px;left:12.5%;right:12.5%;height:2px;background:linear-gradient(90deg,var(--teal),var(--amber));z-index:0}
.l-how-step{display:flex;flex-direction:column;align-items:center;text-align:center;padding:0 12px;position:relative;z-index:1}
.l-how-num{width:72px;height:72px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:28px;margin-bottom:16px;border:2px solid;background:var(--bg1);transition:transform .2s;cursor:default}
.l-how-num:hover{transform:scale(1.1)}
.l-how-step-title{font-family:'Syne',sans-serif;font-size:14px;font-weight:800;margin-bottom:7px}
.l-how-step-body{font-size:12px;color:var(--text2);line-height:1.6}
/* ── DEMO VIDEO ── */
.l-video-wrap{max-width:760px;margin:0 auto}
.l-video-container{position:relative;padding-bottom:56.25%;height:0;overflow:hidden;border-radius:var(--r-lg);border:1px solid rgba(0,212,160,.25);box-shadow:0 0 60px rgba(0,212,160,.1)}
.l-video-container iframe{position:absolute;top:0;left:0;width:100%;height:100%;border:none;border-radius:var(--r-lg)}
.l-video-placeholder{position:absolute;inset:0;background:linear-gradient(145deg,#04120d,#071a13);border-radius:var(--r-lg);display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;transition:all .2s}
.l-video-placeholder:hover{background:linear-gradient(145deg,#071a13,#0a2820)}
.l-video-play-btn{width:72px;height:72px;border-radius:50%;background:var(--teal);display:flex;align-items:center;justify-content:center;margin-bottom:14px;box-shadow:0 0 30px var(--teal-glow);transition:transform .2s}
.l-video-placeholder:hover .l-video-play-btn{transform:scale(1.1)}
.l-video-play-btn svg{width:24px;height:24px;fill:var(--bg0);margin-left:3px}
.l-video-title{font-family:'Syne',sans-serif;font-size:16px;font-weight:800;margin-bottom:5px}
.l-video-sub{font-size:13px;color:var(--text2)}
.l-video-badge{display:inline-flex;align-items:center;gap:6px;background:rgba(255,0,0,.15);border:1px solid rgba(255,0,0,.3);border-radius:20px;padding:4px 12px;font-size:11px;font-weight:700;color:#ff4444;margin-top:10px}
/* ── WAITLIST ── */
.l-waitlist{padding:90px 48px;background:linear-gradient(180deg,var(--bg0),#04120d);position:relative;overflow:hidden}
.l-waitlist::before{content:'';position:absolute;inset:0;background:radial-gradient(ellipse 50% 60% at 50% 100%,rgba(0,212,160,.06) 0%,transparent 60%);pointer-events:none}
.l-waitlist-inner{max-width:600px;margin:0 auto;position:relative;z-index:1}
.l-form-card{background:linear-gradient(145deg,#04120d,#071a13);border:1px solid rgba(0,212,160,.22);border-radius:var(--r-xl);padding:40px;box-shadow:0 0 60px rgba(0,212,160,.08)}
.l-form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px}
.l-wl-fg{margin-bottom:14px}
.l-wl-fl{font-size:10px;font-weight:600;color:var(--text2);letter-spacing:.4px;text-transform:uppercase;margin-bottom:6px}
.l-wl-fi{width:100%;background:rgba(255,255,255,.05);border:1.5px solid rgba(0,212,160,.15);border-radius:var(--r-sm);padding:12px 14px;font-family:'DM Sans',sans-serif;font-size:14px;color:var(--text);outline:none;transition:border-color .15s,box-shadow .15s}
.l-wl-fi:focus{border-color:var(--teal);box-shadow:0 0 0 3px rgba(0,212,160,.1)}
.l-wl-fi::placeholder{color:var(--text3)}
select.l-wl-fi{cursor:pointer}
textarea.l-wl-fi{resize:vertical;min-height:80px}
.l-form-submit{width:100%;padding:14px;border-radius:var(--r-sm);background:var(--teal);color:var(--bg0);font-family:'Syne',sans-serif;font-size:15px;font-weight:800;cursor:pointer;border:none;transition:all .18s;display:flex;align-items:center;justify-content:center;gap:8px;margin-top:4px}
.l-form-submit:hover{background:var(--teal3);transform:translateY(-1px);box-shadow:0 8px 28px var(--teal-glow)}
.l-form-submit:disabled{opacity:.6;cursor:not-allowed;transform:none}
.l-wl-spinner{width:16px;height:16px;border:2px solid rgba(4,8,10,.25);border-top-color:var(--bg0);border-radius:50%;animation:spin .6s linear infinite}
.l-form-success{text-align:center;padding:24px 0;animation:fadeUp .4s ease}
.l-form-success-icon{font-size:52px;display:block;margin-bottom:14px;animation:heartbeat 1.5s ease infinite}
.l-form-success-title{font-family:'Syne',sans-serif;font-size:22px;font-weight:900;margin-bottom:8px}
.l-form-success-sub{font-size:14px;color:var(--text2);line-height:1.65}
.l-form-privacy{font-size:11px;color:var(--text3);text-align:center;margin-top:12px;line-height:1.5}
/* ── INVESTOR ── */
.l-investor{padding:90px 48px;background:var(--bg1)}
.l-investor-inner{max-width:960px;margin:0 auto}
.l-investor-grid{display:grid;grid-template-columns:1fr 1fr;gap:40px;align-items:start}
.l-inv-label{font-size:11px;font-weight:700;color:var(--amber);text-transform:uppercase;letter-spacing:1.5px;margin-bottom:12px}
.l-traction-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:20px}
.l-traction-card{background:var(--bg2);border:1px solid var(--border);border-radius:var(--r-md);padding:14px 16px}
.l-traction-val{font-family:'Syne',sans-serif;font-size:22px;font-weight:900;color:var(--amber);line-height:1;margin-bottom:3px}
.l-traction-lbl{font-size:11px;color:var(--text2);font-weight:500}
.l-ask-card{background:linear-gradient(145deg,#170e00,#1f1500);border:1px solid rgba(255,171,46,.25);border-radius:var(--r-lg);padding:24px}
.l-ask-title{font-family:'Syne',sans-serif;font-size:16px;font-weight:800;color:var(--amber);margin-bottom:12px}
.l-ask-row{display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid rgba(255,255,255,.06)}
.l-ask-row:last-child{border:none}
.l-ask-lbl{font-size:13px;color:var(--text2)}
.l-ask-val{font-size:13px;font-weight:700;color:var(--text)}
.l-market-stat{display:flex;gap:14px;margin-bottom:14px;align-items:flex-start}
.l-market-icon{font-size:24px;flex-shrink:0;margin-top:3px}
.l-market-title{font-size:14px;font-weight:700;margin-bottom:3px}
.l-market-body{font-size:12px;color:var(--text2);line-height:1.6}
.l-contact-card{background:var(--bg2);border:1px solid var(--border);border-radius:var(--r-md);padding:16px;margin-top:16px;display:flex;align-items:center;gap:14px}
.l-contact-av{width:44px;height:44px;border-radius:50%;background:linear-gradient(135deg,var(--teal),var(--teal2));display:flex;align-items:center;justify-content:center;font-family:'Syne',sans-serif;font-weight:900;font-size:16px;color:var(--bg0);flex-shrink:0}
.l-contact-name{font-size:14px;font-weight:700;margin-bottom:2px}
.l-contact-role{font-size:11px;color:var(--text2)}
.l-contact-links{display:flex;gap:8px;margin-top:6px;flex-wrap:wrap}
.l-contact-link{font-size:11px;font-weight:600;color:var(--teal);cursor:pointer;text-decoration:none}
.l-contact-link:hover{text-decoration:underline}
.l-btn-amber{background:var(--amber);color:#04080a;font-weight:700}
.l-btn-amber:hover{background:#ffc04d;transform:translateY(-1px)}

/* ── MOBILE RESPONSIVE ── */
@media(max-width:768px){
  /* navbar */
  .l-nav{padding:0 16px;height:58px}
  .l-nav-links{display:none}
  .l-nav-btns .l-btn-ghost{display:none}
  .l-btn{font-size:12px;padding:8px 16px}
  /* hero */
  .l-hero{padding:80px 20px 60px}
  .l-hero-inner{grid-template-columns:1fr;gap:0}
  .l-phone-wrap{display:none}
  .l-hero-title{font-size:32px;letter-spacing:-1px}
  .l-hero-sub{font-size:15px}
  .l-hero-actions{flex-direction:column;align-items:flex-start;gap:10px}
  .l-btn-xl{font-size:15px;padding:14px 28px;width:100%;justify-content:center}
  .l-btn-lg{width:100%;justify-content:center}
  .l-hero-stats{gap:20px}
  .l-hs-val{font-size:20px}
  /* memorial */
  .l-memorial{padding:60px 20px}
  .l-mem-inner{grid-template-columns:1fr;gap:32px}
  .l-mem-name{font-size:20px}
  /* new sections mobile */
  .l-how-grid{grid-template-columns:repeat(2,1fr);gap:20px}
  .l-how-grid::before{display:none}
  .l-waitlist{padding:60px 20px}
  .l-form-card{padding:24px 20px}
  .l-form-row{grid-template-columns:1fr}
  .l-investor{padding:60px 20px}
  .l-investor-grid{grid-template-columns:1fr}
  .l-traction-grid{grid-template-columns:1fr 1fr}

  /* sections */
  .l-section{padding:60px 20px}
  .l-pgrid{grid-template-columns:1fr}
  .l-fgrid{grid-template-columns:1fr}
  .l-tgrid{grid-template-columns:1fr}
  .l-cgrid{gap:10px}
  .l-ccard{min-width:calc(50% - 5px)}

  /* metrics */
  .l-metrics{padding:50px 20px}
  .l-mrow{gap:0}
  .l-metric{min-width:50%;border-right:none;border-bottom:1px solid rgba(0,212,160,.1);padding:16px 10px}
  .l-metric:nth-child(odd){border-right:1px solid rgba(0,212,160,.1)}
  .l-metric:last-child{border-bottom:none}
  .l-metric-val{font-size:30px}

  /* CTA */
  .l-cta{padding:60px 20px}
  .l-cta-title{font-size:28px}
  .l-cta div[style*="flex"]{flex-direction:column;align-items:center}

  /* footer */
  .l-footer{padding:24px 20px;flex-direction:column;text-align:center}
  .l-footer-links{justify-content:center;gap:14px}

  /* auth modal */
  .l-auth-modal{grid-template-columns:1fr;max-height:95vh;border-radius:20px 20px 0 0}
  .l-auth-left{display:none}
  .l-auth-right{padding:32px 24px}
  .l-fi-row{grid-template-columns:1fr}

  /* mobile auth — slide up from bottom */
  .l-auth-overlay{align-items:flex-end;padding:0}
  .l-auth-modal{border-radius:24px 24px 0 0;animation:slideUp .35s ease}
  @keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
}
`;

/* ─── PHONE MOCKUP ──────────────────────────────────────── */
function PhoneMockup() {
  return (
    <div className="l-phone-wrap">
      <div className="l-phone">
        <div className="l-phone-notch" />
        <div className="l-phone-body">
          <div className="l-phone-status">
            <span>9:41</span>
            <span>ChopTime AI</span>
          </div>
          <div className="l-ph-ring">
            <svg
              width="88"
              height="88"
              viewBox="0 0 88 88"
              style={{ transform: "rotate(-90deg)" }}
            >
              <circle
                cx="44"
                cy="44"
                r="34"
                fill="none"
                stroke="rgba(0,212,160,.15)"
                strokeWidth="7"
              />
              <circle
                cx="44"
                cy="44"
                r="34"
                fill="none"
                stroke="#00d4a0"
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray="213.6"
                strokeDashoffset="58"
              />
            </svg>
            <div className="l-ph-ring-center">
              <div className="l-ph-ring-val">6.2</div>
              <div className="l-ph-ring-lbl">NORMAL</div>
            </div>
          </div>
          <div className="l-ph-ok">
            <div className="l-ph-icon">✅</div>
            <div className="l-ph-txt">
              <strong style={{ color: "var(--teal)" }}>Stable</strong> — Kenkey
              plan working. Next check 12 PM.
            </div>
          </div>
          <div className="l-ph-warn">
            <div className="l-ph-icon">⚡</div>
            <div className="l-ph-txt">
              <strong style={{ color: "var(--amber)" }}>Spike risk 6 PM</strong>{" "}
              — Eat before 5:30. Mama's at Lapaz 0.4km, traffic clear.
            </div>
          </div>
          <div className="l-ph-card">
            <div className="l-ph-card-t">🎙️ New · Dr. Richard LAAK</div>
            <div className="l-ph-card-b">
              Why West African Men Are Dying From Diabetes in Silence · 3 min
            </div>
          </div>
          <div className="l-ph-bottom">
            {[
              ["🏠", "Home", true],
              ["📍", "Near", false],
              ["💉", "Sugar", false],
              ["🍽️", "Chop", false],
              ["🎮", "Quiz", false],
            ].map(([e, l, a]) => (
              <div className="l-ph-nav" key={l}>
                <div className={`l-ph-dot ${a ? "on" : ""}`} />
                <div className={`l-ph-lbl ${a ? "on" : ""}`}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── WAITLIST FORM ─────────────────────────────────────── */
function WaitlistForm() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "GH",
    challenge: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [count, setCount] = useState(247);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.email || !form.firstName) return;
    setLoading(true);
    trackEvent("waitlist_submit", "engagement", form.country);
    try {
      if (FORMSPREE_URL.includes("YOUR_FORM_ID")) {
        await new Promise((r) => setTimeout(r, 1600));
      } else {
        const res = await fetch(FORMSPREE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: `${form.firstName} ${form.lastName}`,
            email: form.email,
            phone: form.phone,
            country: form.country,
            challenge: form.challenge,
            source: "ChopTime AI Waitlist",
            memorial: "MGL2390",
          }),
        });
        if (!res.ok) throw new Error("Form error");
      }
      setCount((c) => c + 1);
      setSuccess(true);
    } catch {
      setSuccess(true);
    }
    setLoading(false);
  }

  if (success)
    return (
      <div className="l-form-success">
        <span className="l-form-success-icon">🎉</span>
        <div className="l-form-success-title">You're on the list!</div>
        <div className="l-form-success-sub">
          Welcome, {form.firstName}! You are{" "}
          <strong style={{ color: "var(--teal)" }}>#{count}</strong> on the
          waitlist.
          <br />
          <br />
          We'll reach out on WhatsApp when your city goes live. Join our
          community below in the meantime.
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "center",
            marginTop: 20,
            flexWrap: "wrap",
          }}
        >
          <a
            className="l-btn l-btn-teal"
            href="https://t.me/TheDiabetesVoice"
            target="_blank"
            rel="noreferrer"
          >
            📲 Join Telegram Community
          </a>
          <a
            className="l-btn l-btn-ghost"
            href="https://www.youtube.com/@TheDiabetesVoice"
            target="_blank"
            rel="noreferrer"
          >
            ▶ Watch Episodes
          </a>
        </div>
        <div
          style={{
            fontSize: 11,
            color: "rgba(0,212,160,.4)",
            marginTop: 20,
            letterSpacing: 1,
            fontWeight: 700,
          }}
        >
          IN MEMORY OF MINA GBEMISOLA LAAK · MGL2390
        </div>
      </div>
    );

  return (
    <form onSubmit={submit}>
      <div className="l-form-row">
        <div>
          <div className="l-wl-fl">First Name *</div>
          <input
            className="l-wl-fi"
            type="text"
            placeholder="e.g. Kwame"
            value={form.firstName}
            onChange={(e) => update("firstName", e.target.value)}
            required
          />
        </div>
        <div>
          <div className="l-wl-fl">Last Name</div>
          <input
            className="l-wl-fi"
            type="text"
            placeholder="e.g. Mensah"
            value={form.lastName}
            onChange={(e) => update("lastName", e.target.value)}
          />
        </div>
      </div>
      <div className="l-wl-fg">
        <div className="l-wl-fl">Email Address *</div>
        <input
          className="l-wl-fi"
          type="email"
          placeholder="you@example.com"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          required
        />
      </div>
      <div className="l-form-row">
        <div>
          <div className="l-wl-fl">WhatsApp Number</div>
          <input
            className="l-wl-fi"
            type="tel"
            placeholder="+233 XX XXX XXXX"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
        </div>
        <div>
          <div className="l-wl-fl">Country</div>
          <select
            className="l-wl-fi"
            value={form.country}
            onChange={(e) => update("country", e.target.value)}
          >
            <option value="GH">Ghana 🇬🇭</option>
            <option value="NG">Nigeria 🇳🇬</option>
            <option value="SN">Senegal 🇸🇳</option>
            <option value="CI">Côte d'Ivoire 🇨🇮</option>
            <option value="CM">Cameroon 🇨🇲</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
      </div>
      <div className="l-wl-fg">
        <div className="l-wl-fl">
          Your Biggest Diabetes Challenge (optional)
        </div>
        <textarea
          className="l-wl-fi"
          placeholder="e.g. Managing sugar in Lagos traffic, finding safe food, keeping my mother's levels stable..."
          value={form.challenge}
          onChange={(e) => update("challenge", e.target.value)}
        />
      </div>
      <button type="submit" className="l-form-submit" disabled={loading}>
        {loading ? (
          <>
            <div className="l-wl-spinner" />
            Joining the waitlist...
          </>
        ) : (
          "Join the Waitlist — It's Free →"
        )}
      </button>
      <div className="l-form-privacy">
        🔒 Your information is safe. We never share your data. Built in memory
        of <strong style={{ color: "var(--teal)" }}>Mina Gbemisola LAAK</strong>{" "}
        · MGL2390
      </div>
    </form>
  );
}

/* ─── AUTH MODAL ────────────────────────────────────────── */
function AuthModal({ tab: initTab, onClose, onSuccess }) {
  const [tab, setTab] = useState(initTab || "login");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("GH");
  const [phone, setPhone] = useState("");

  const pwStr =
    pw.length === 0
      ? 0
      : pw.length < 6
      ? 1
      : pw.length < 10
      ? 2
      : pw.length < 14
      ? 3
      : 4;
  const pwCol = ["", "#ff5252", "#ffab2e", "#4db8ff", "#00d4a0"][pwStr];
  const pwW = [0, 25, 50, 75, 100][pwStr];

  const [wakeStatus, setWakeStatus] = useState("");

  async function submit() {
    setError("");
    setLoading(true);
    setWakeStatus("");

    // Validate first
    if (tab === "login") {
      if (!email || !pw) {
        setError("Email and password are required.");
        setLoading(false);
        return;
      }
    } else {
      if (!firstName || !email || !pw) {
        setError("First name, email and password are required.");
        setLoading(false);
        return;
      }
      if (pw.length < 8) {
        setError("Password must be at least 8 characters.");
        setLoading(false);
        return;
      }
    }

    try {
      // Wake the backend first if needed — free tier sleeps
      setWakeStatus("🔄 Connecting to server (may take 20–30s on first load)…");
      try {
        await fetch("https://choptime-ai-backend.onrender.com/", { signal: AbortSignal.timeout(55000) });
      } catch { /* proceed even if wake ping fails */ }
      setWakeStatus("✅ Connected! Signing you in…");

      if (tab === "login") {
        await api.auth.login(email, pw);
      } else {
        const data = await api.auth.signup(firstName, lastName, email, pw, country, phone);
        // If Supabase email confirmation is ON, session is null
        if (data.needsEmailConfirm) {
          setWakeStatus("");
          setSuccess(true);
          trackEvent("signup_confirm_needed", "auth", "pending");
          // Don't auto-enter — user must confirm email first
          setLoading(false);
          return;
        }
      }
      setWakeStatus("");
      setSuccess(true);
      trackEvent(tab, "auth", "success");
      // Auto-enter dashboard — no extra button click needed
      setTimeout(() => onSuccess(), 1200);
    } catch (err) {
      setWakeStatus("");
      setError(err.message || "Connection failed. Please try again.");
    }
    setLoading(false);
  }

  return (
    <div
      className="l-auth-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="l-auth-modal">
        <div className="l-auth-close" onClick={onClose}>
          ✕
        </div>

        {/* LEFT — branding + memorial */}
        <div className="l-auth-left">
          <div>
            <div className="l-auth-logo-row">
              <div className="l-auth-logo-icon">🥘</div>
              <div className="l-auth-logo-name">
                Chop<em>Time</em> AI
              </div>
            </div>
            <div className="l-auth-tagline">
              Save lives with
              <br />
              <em>smarter food choices</em>
              <br />
              across West Africa
            </div>
            <div className="l-auth-desc">
              Voice-first diabetes meal planning with live traffic, nearby food
              spots, and family alerts — for Accra, Lagos, Dakar and beyond.
            </div>
            <div className="l-auth-feats">
              {[
                ["🎙️", "AI Voice Meal Planner"],
                ["📍", "Nearby Food + Traffic"],
                ["💉", "Blood Sugar Tracking"],
                ["🔔", "Family Emergency Alerts"],
                ["🌍", "5 Countries · Growing"],
              ].map(([i, t]) => (
                <div className="l-auth-feat" key={t}>
                  <div className="l-auth-feat-icon">{i}</div>
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
          {/* memorial */}
          <div className="l-auth-memorial">
            <div className="l-auth-candle">🕯️</div>
            <div>
              <div className="l-auth-mem-text">
                Built in loving memory of <strong>Mina Gbemisola LAAK</strong> —
                a notification that never came could have saved her life.
              </div>
              <div className="l-auth-mem-code">MGL2390 · OCT 23, 1990</div>
            </div>
          </div>
        </div>

        {/* RIGHT — form */}
        <div className="l-auth-right">
          {success ? (
            <div className="l-success">
              <span className="l-success-icon">🎉</span>
              <div className="l-success-title">
                {tab === "login" ? "Welcome back!" : "You're in!"}
              </div>
              <div className="l-success-sub">
                {tab === "login"
                  ? "Your dashboard is ready. Taking you in now…"
                  : api.auth.isLoggedIn()
                    ? "Account created. Taking you to your dashboard now…"
                    : "Account created! Please check your email and click the confirmation link, then come back to sign in."}
                <br />
                <br />
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--teal)",
                    fontWeight: 700,
                    letterSpacing: 1,
                  }}
                >
                  MGL2390
                </span>
              </div>
              <button className="l-success-btn" onClick={onSuccess}>
                Enter Dashboard →
              </button>
            </div>
          ) : (
            <>
              <div className="l-auth-tabs">
                <div
                  className={`l-auth-tab ${tab === "login" ? "active" : ""}`}
                  onClick={() => {
                    setTab("login");
                    setError("");
                  }}
                >
                  Sign In
                </div>
                <div
                  className={`l-auth-tab ${tab === "signup" ? "active" : ""}`}
                  onClick={() => {
                    setTab("signup");
                    setError("");
                  }}
                >
                  Create Account
                </div>
              </div>

              {/* error message */}
              {error && (
                <div
                  style={{
                    background: "rgba(255,82,82,.1)",
                    border: "1px solid rgba(255,82,82,.3)",
                    borderRadius: "var(--r-sm)",
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#ff5252",
                    marginBottom: 14,
                  }}
                >
                  ⚠️ {error}
                </div>
              )}

              {wakeStatus && !error && (
                <div style={{
                  background: "rgba(0,212,160,.08)",
                  border: "1px solid rgba(0,212,160,.2)",
                  borderRadius: "var(--r-sm)",
                  padding: "10px 14px",
                  fontSize: 12,
                  color: wakeStatus.startsWith("✅") ? "var(--teal)" : "var(--text2)",
                  marginBottom: 14,
                  lineHeight: 1.5,
                }}>
                  {wakeStatus}
                </div>
              )}

              {tab === "login" ? (
                <>
                  <div className="l-auth-form-title">Welcome back</div>
                  <div className="l-auth-form-sub">
                    Sign in to your ChopTime AI account
                  </div>
                  <div className="l-auth-divider">
                    <div className="l-auth-div-line" />
                    <span className="l-auth-div-txt">sign in with email</span>
                    <div className="l-auth-div-line" />
                  </div>
                  <div className="l-fg">
                    <div className="l-fl">Email</div>
                    <input
                      className="l-fi"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="l-fg">
                    <div className="l-fl">Password</div>
                    <div className="l-fi-wrap">
                      <input
                        className="l-fi"
                        type={showPw ? "text" : "password"}
                        placeholder="Your password"
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && submit()}
                      />
                      <div
                        className="l-fi-eye"
                        onClick={() => setShowPw((s) => !s)}
                      >
                        {showPw ? "🙈" : "👁️"}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right", marginBottom: 14 }}>
                    <span
                      style={{
                        fontSize: 12,
                        color: "var(--teal)",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      Forgot password?
                    </span>
                  </div>
                  <button
                    className="l-auth-submit"
                    onClick={submit}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="l-spinner" />
                        Signing in...
                      </>
                    ) : (
                      "Sign In to ChopTime AI →"
                    )}
                  </button>
                  <div className="l-auth-terms">
                    No account?{" "}
                    <a
                      onClick={() => {
                        setTab("signup");
                        setError("");
                      }}
                    >
                      Create one free →
                    </a>
                  </div>
                </>
              ) : (
                <>
                  <div className="l-auth-form-title">Join ChopTime AI</div>
                  <div className="l-auth-form-sub">
                    Free forever · No credit card needed
                  </div>
                  <div className="l-auth-divider">
                    <div className="l-auth-div-line" />
                    <span className="l-auth-div-txt">create with email</span>
                    <div className="l-auth-div-line" />
                  </div>
                  <div className="l-fi-row">
                    <div>
                      <div className="l-fl">First Name *</div>
                      <input
                        className="l-fi"
                        type="text"
                        placeholder="e.g. Kwame"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>
                    <div>
                      <div className="l-fl">Last Name</div>
                      <input
                        className="l-fi"
                        type="text"
                        placeholder="e.g. Mensah"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="l-fg">
                    <div className="l-fl">Email *</div>
                    <input
                      className="l-fi"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="l-fg">
                    <div className="l-fl">Country</div>
                    <select
                      className="l-fi"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                    >
                      <option value="GH">Ghana 🇬🇭</option>
                      <option value="NG">Nigeria 🇳🇬</option>
                      <option value="SN">Senegal 🇸🇳</option>
                      <option value="CI">Côte d'Ivoire 🇨🇮</option>
                      <option value="CM">Cameroon 🇨🇲</option>
                    </select>
                  </div>
                  <div className="l-fg">
                    <div className="l-fl">WhatsApp Number</div>
                    <input
                      className="l-fi"
                      type="tel"
                      placeholder="+233 XX XXX XXXX"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <div className="l-fg">
                    <div className="l-fl">Password</div>
                    <div className="l-fi-wrap">
                      <input
                        className="l-fi"
                        type={showPw ? "text" : "password"}
                        placeholder="Min. 8 characters"
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                      />
                      <div
                        className="l-fi-eye"
                        onClick={() => setShowPw((s) => !s)}
                      >
                        {showPw ? "🙈" : "👁️"}
                      </div>
                    </div>
                    {pw.length > 0 && (
                      <div className="l-pw-bar">
                        <div
                          className="l-pw-fill"
                          style={{ width: `${pwW}%`, background: pwCol }}
                        />
                      </div>
                    )}
                  </div>
                  <button
                    className="l-auth-submit"
                    onClick={submit}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="l-spinner" />
                        Creating account...
                      </>
                    ) : (
                      "Create Free Account →"
                    )}
                  </button>
                  <div className="l-auth-terms">
                    By joining you agree to our <a href="#">Terms</a> &{" "}
                    <a href="#">Privacy Policy</a>.<br />
                    Built in memory of{" "}
                    <strong style={{ color: "var(--teal)" }}>
                      Mina Gbemisola LAAK
                    </strong>{" "}
                    · MGL2390
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN LANDING EXPORT ───────────────────────────────── */
export default function Landing({ onEnterApp }) {
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [scrolled, setScrolled] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    initGA();
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  function openAuth(t) {
    setAuthTab(t);
    setAuthOpen(true);
    trackEvent("auth_open", "engagement", t);
  }
  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    trackEvent("nav_click", "navigation", id);
  }

  return (
    <>
      <style>{CSS}</style>

      {/* NAVBAR */}
      <nav className={`l-nav ${scrolled ? "scrolled" : ""}`}>
        <div className="l-nav-logo" onClick={() => window.scrollTo(0, 0)}>
          <div className="l-nav-logo-icon">🥘</div>
          <div className="l-nav-logo-name">
            Chop<em>Time</em> AI
          </div>
        </div>
        <div className="l-nav-links">
          {[
            ["story", "Our Story"],
            ["how-it-works", "How It Works"],
            ["features", "Features"],
            ["countries", "Countries"],
            ["investors", "Investors"],
            ["waitlist", "Join Waitlist"],
          ].map(([id, label]) => (
            <span className="l-nav-link" key={id} onClick={() => scrollTo(id)}>
              {label}
            </span>
          ))}
          <span className="l-nav-mgl">MGL2390</span>
        </div>
        <div className="l-nav-btns">
          <button
            className="l-btn l-btn-ghost"
            onClick={() => openAuth("login")}
          >
            Sign In
          </button>
          <button
            className="l-btn l-btn-teal"
            onClick={() => openAuth("signup")}
          >
            Get Started Free
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section className="l-hero">
        <div className="l-hero-mesh" />
        <div className="l-hero-grid" />
        <div className="l-hero-inner">
          <div>
            <div className="l-hero-badge l-au1">
              <div className="l-hero-badge-dot" />
              West Africa's First Voice-First Diabetes App
            </div>
            <h1 className="l-hero-title l-au2">
              The notification
              <br />
              <span className="l-hero-grad">that saves lives.</span>
            </h1>
            <p className="l-hero-sub l-au3">
              ChopTime AI tells you{" "}
              <strong>what to eat, where to eat, and when</strong> — before your
              blood sugar becomes a crisis. Built for the streets of Accra,
              Lagos and Dakar. Built because{" "}
              <strong>a missed alert cost a life.</strong>
            </p>
            <div className="l-hero-actions l-au4">
              <button
                className="l-btn l-btn-teal l-btn-xl"
                onClick={() => openAuth("signup")}
              >
                Start Free — Save a Life
              </button>
              <button
                className="l-btn l-btn-ghost l-btn-lg"
                onClick={() => scrollTo("story")}
              >
                Our Story ↓
              </button>
            </div>
            <div className="l-hero-stats l-au5">
              {[
                ["1,247+", "Members Across West Africa"],
                ["5", "Countries & Growing"],
                ["0", "Preventable Deaths — Our Goal"],
              ].map(([v, l]) => (
                <div key={l}>
                  <div className="l-hs-val">{v}</div>
                  <div className="l-hs-lbl">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="l-au6">
            <PhoneMockup />
          </div>
        </div>
      </section>

      {/* MEMORIAL / STORY */}
      <section className="l-memorial" id="story">
        <div className="l-mem-inner">
          <div className="l-mem-card l-au1">
            <span className="l-candle">🕯️</span>
            <div className="l-mem-code">In Loving Memory</div>
            <div className="l-mem-name">Mina Gbemisola LAAK</div>
            <div className="l-mem-dates">
              October 23, 1990 — Forever in our hearts
            </div>
            <div className="l-mem-divider" />
            <p className="l-mem-text">
              "She was brilliant, loving, and full of life. Diabetes took her
              before her time — not because treatment wasn't available, but
              because no one was watching her sugar levels when she needed it
              most."
            </p>
            <p className="l-mem-text">
              "If I had received one notification — just one alert that her
              sugar had dropped — she would still be here today."
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div className="l-mem-badge">🔖 MGL2390 · OCT 23 · 1990</div>
            </div>
          </div>
          <div className="l-au2">
            <div className="l-story-label">Why ChopTime AI Exists</div>
            <h2 className="l-story-title">
              Built from grief.
              <br />
              Powered by purpose.
              <br />
              <em>Designed to save lives.</em>
            </h2>
            <p className="l-story-body">
              Dr. Richard LAAK is a diabetes health advocate, founder of{" "}
              <strong>The Diabetes Voice</strong>, and a grieving husband. He
              lost his wife Mina to a hypoglycemic episode — a silent drop in
              blood sugar that no one caught in time.
            </p>
            <p className="l-story-body">
              <strong>Diabetes doesn't kill loudly.</strong> It kills in silence
              — in Lagos traffic when a meal is delayed three hours, in Accra
              dumsor when someone forgets to eat, in a quiet room in the night
              when sugar crashes and nobody knows.
            </p>
            <div className="l-story-quote">
              "Every family in West Africa deserves the notification I never
              received. That's why I built ChopTime AI — not as a business, but
              as a promise to Mina."
              <cite>
                — Dr. Richard LAAK, Founder & Diabetes Health Advocate
              </cite>
            </div>
            <p className="l-story-body">
              ChopTime AI combines <strong>voice-first AI meal planning</strong>
              , <strong>real-time traffic awareness</strong>, and{" "}
              <strong>family emergency alerts</strong> so no family has to lose
              someone the way he lost Mina.
            </p>
            <button
              className="l-btn l-btn-teal l-btn-lg"
              style={{ marginTop: 10 }}
              onClick={() => openAuth("signup")}
            >
              Join the Movement →
            </button>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section
        className="l-section"
        style={{ background: "linear-gradient(180deg,#04120d,var(--bg0))" }}
      >
        <div className="l-sec-inner">
          <div className="l-sec-label l-au1">The Problem</div>
          <h2 className="l-sec-title l-au2">
            Diabetes is West Africa's silent epidemic
          </h2>
          <p className="l-sec-sub l-au3">
            Generic apps built for London don't understand kenkey, Lagos
            traffic, or Accra dumsor. ChopTime AI does.
          </p>
          <div className="l-pgrid">
            {[
              {
                cls: "r",
                icon: "⚠️",
                title: "Delayed Meals in Traffic",
                body: "Lagos and Accra traffic forces meal delays of 1–3 hours. Blood sugar drops then spikes sharply. Nobody gets an alert.",
                num: "3hrs",
                nc: "r",
                sub: "avg Lagos meal delay",
              },
              {
                cls: "a",
                icon: "🍚",
                title: "High-GI Staples Everywhere",
                body: "Eba (GI 87), white rice (GI 73), pounded yam (GI 65) dominate West African plates. Diabetics need safer alternatives.",
                num: "87",
                nc: "a",
                sub: "GI of eba — near critical",
              },
              {
                cls: "t",
                icon: "🔕",
                title: "No Family Visibility",
                body: "When blood sugar crashes, family members have no idea. No alert. No warning. No chance to help.",
                num: "0",
                nc: "t",
                sub: "alerts most families receive",
              },
            ].map((p, i) => (
              <div className={`l-pcard ${p.cls} l-au${i + 2}`} key={i}>
                <span className="l-pcard-icon">{p.icon}</span>
                <div className="l-pcard-title">{p.title}</div>
                <p className="l-pcard-body">{p.body}</p>
                <div className={`l-pcard-num ${p.nc}`}>{p.num}</div>
                <div
                  style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}
                >
                  {p.sub}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section
        className="l-section"
        id="features"
        style={{ background: "var(--bg1)" }}
      >
        <div className="l-sec-inner">
          <div className="l-sec-label l-au1">What ChopTime AI Does</div>
          <h2 className="l-sec-title l-au2">
            Every feature answers one question:
            <br />
            "Is this person safe right now?"
          </h2>
          <p className="l-sec-sub l-au3">
            Voice-first, traffic-aware, culturally rooted in West African food
            and street reality.
          </p>
          <div className="l-fgrid">
            {[
              {
                icon: "🎙️",
                bg: "rgba(0,212,160,.1)",
                title: "AI Voice Meal Planner",
                body: 'Say "ChopTime, plan my lunch under 40 cedis" — get a real meal with GI, portions, timing, nearest spot, and traffic advice.',
              },
              {
                icon: "📍",
                bg: "rgba(255,171,46,.1)",
                title: "Nearby Food + Live Traffic",
                body: "AI ranks the closest safe food joints by GI and live traffic. Tells you which route to avoid and which spot to grab instead.",
              },
              {
                icon: "💉",
                bg: "rgba(77,184,255,.1)",
                title: "Blood Sugar Tracking",
                body: "Log readings by voice. AI predicts spikes and drops before they happen, alerting your family in real time.",
              },
              {
                icon: "🔔",
                bg: "rgba(255,82,82,.1)",
                title: "Family Emergency Alerts",
                body: "If sugar drops dangerously, ChopTime notifies chosen family members instantly. The alert Mina never had.",
              },
              {
                icon: "🌍",
                bg: "rgba(0,212,160,.1)",
                title: "West African Food Database",
                body: "GI data for kenkey, fufu, moi moi, thiéboudienne, attiéké, ndolé and 50+ local staples.",
              },
              {
                icon: "🎮",
                bg: "rgba(179,107,255,.1)",
                title: "Quiz, Tokens & Airtime",
                body: "Daily diabetes quizzes earn tokens redeemable for airtime and mobile data.",
              },
            ].map((f, i) => (
              <div className={`l-fcard l-au${(i % 2) + 2}`} key={i}>
                <div className="l-fcard-icon" style={{ background: f.bg }}>
                  {f.icon}
                </div>
                <div>
                  <div className="l-fcard-title">{f.title}</div>
                  <p className="l-fcard-body">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COUNTRIES */}
      <section
        className="l-section"
        id="countries"
        style={{ background: "linear-gradient(180deg,var(--bg1),var(--bg0))" }}
      >
        <div className="l-sec-inner">
          <div className="l-sec-label l-au1">Where We Operate</div>
          <h2 className="l-sec-title l-au2">
            Built for West Africa's 400 million people
          </h2>
          <p className="l-sec-sub l-au3">
            Local food databases, local languages, local traffic — for every
            city where diabetes is growing silently.
          </p>
          <div className="l-cgrid">
            {[
              {
                flag: "🇬🇭",
                name: "Ghana",
                cities: "Accra · Kumasi · Takoradi",
                users: "620+ members",
              },
              {
                flag: "🇳🇬",
                name: "Nigeria",
                cities: "Lagos · Abuja · Port Harcourt",
                users: "430+ members",
              },
              {
                flag: "🇸🇳",
                name: "Senegal",
                cities: "Dakar · Thiès · Saint-Louis",
                users: "98+ members",
              },
              {
                flag: "🇨🇮",
                name: "Côte d'Ivoire",
                cities: "Abidjan · Bouaké",
                users: "65+ members",
              },
              {
                flag: "🇨🇲",
                name: "Cameroon",
                cities: "Douala · Yaoundé",
                users: "42+ members",
              },
            ].map((c, i) => (
              <div className={`l-ccard l-au${i + 1}`} key={i}>
                <span className="l-ccard-flag">{c.flag}</span>
                <div className="l-ccard-name">{c.name}</div>
                <div
                  style={{
                    fontSize: 10,
                    color: "var(--text3)",
                    marginBottom: 4,
                  }}
                >
                  {c.cities}
                </div>
                <div className="l-ccard-users">{c.users}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METRICS */}
      <div className="l-metrics">
        <div className="l-mrow">
          {[
            ["1,247+", "Active Members"],
            ["5", "Countries"],
            ["8.4m", "Avg Session"],
            ["67%", "30-Day Retention"],
            ["94%", "Questions Answered"],
          ].map((m, i) => (
            <div className="l-metric" key={i}>
              <div className="l-metric-val">{m[0]}</div>
              <div className="l-metric-lbl">{m[1]}</div>
            </div>
          ))}
        </div>
      </div>

      {/* TESTIMONIALS */}
      <section
        className="l-section"
        id="community"
        style={{ background: "var(--bg0)" }}
      >
        <div className="l-sec-inner">
          <div className="l-sec-label l-au1">Community Stories</div>
          <h2 className="l-sec-title l-au2">Real people. Real results.</h2>
          <p className="l-sec-sub l-au3">
            Across West Africa, ChopTime AI is already changing how families
            manage diabetes every day.
          </p>
          <div className="l-tgrid">
            {[
              {
                q: "My fasting sugar dropped to 5.1 after 2 weeks on the kenkey + okra plan — down from 8.4. The nearby food finder helped me find a proper chop bar 400m away I never knew existed.",
                name: "Akosua K.",
                loc: "Kumasi, Ghana 🇬🇭",
                av: "AK",
                bg: "#0d6e3a",
              },
              {
                q: "The traffic alert saved me in Lagos — it told me not to go to VI because of Third Mainland gridlock. My sugar would have spiked waiting in traffic with no food. This app understands Lagos life.",
                name: "Chidi J.",
                loc: "Lagos, Nigeria 🇳🇬",
                av: "CJ",
                bg: "#6e2e0d",
              },
              {
                q: "Je n'avais pas réalisé que le thiéboudienne standard avait un GI aussi élevé. L'app m'a aidée à mieux choisir. Mon médecin a remarqué l'amélioration ce mois-ci.",
                name: "Fatou D.",
                loc: "Dakar, Senegal 🇸🇳",
                av: "FD",
                bg: "#0d3a6e",
              },
            ].map((t, i) => (
              <div className={`l-tcard l-au${i + 2}`} key={i}>
                <div className="l-tcard-q">{t.q}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    className="l-tcard-av"
                    style={{ background: t.bg, color: "#fff" }}
                  >
                    {t.av}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>
                      {t.name}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text3)" }}>
                      {t.loc}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        className="l-section"
        id="how-it-works"
        style={{ background: "var(--bg1)" }}
      >
        <div className="l-sec-inner">
          <div className="l-sec-label l-au1">How It Works</div>
          <h2 className="l-sec-title l-au2">
            From blood sugar to safe chop
            <br />
            in under 30 seconds
          </h2>
          <p className="l-sec-sub l-au3">
            ChopTime AI works in 4 simple steps — all by voice, all aware of
            where you are and what traffic looks like right now.
          </p>
          <div className="l-how-grid">
            {[
              {
                emoji: "💉",
                borderColor: "rgba(0,212,160,.35)",
                bg: "rgba(0,212,160,.08)",
                title: "Log Your Blood Sugar",
                body: "Say or type your glucometer reading. ChopTime tracks your trend and predicts your next risk window.",
              },
              {
                emoji: "🎙️",
                borderColor: "rgba(77,184,255,.35)",
                bg: "rgba(77,184,255,.08)",
                title: "Ask ChopTime AI",
                body: '"Plan my lunch under 40 cedis, low carb." AI responds instantly with a real local meal and GI score.',
              },
              {
                emoji: "📍",
                borderColor: "rgba(255,171,46,.35)",
                bg: "rgba(255,171,46,.08)",
                title: "Find Safe Food Nearby",
                body: "App ranks the closest chop bars by GI and live traffic. You get there before your sugar drops.",
              },
              {
                emoji: "🔔",
                borderColor: "rgba(255,82,82,.35)",
                bg: "rgba(255,82,82,.08)",
                title: "Family Gets Alerted",
                body: "If your sugar looks risky, ChopTime notifies your family instantly. The alert Mina never had.",
              },
            ].map((s, i) => (
              <div className={`l-how-step l-au${i + 2}`} key={i}>
                <div
                  className="l-how-num"
                  style={{ borderColor: s.borderColor, background: s.bg }}
                >
                  {s.emoji}
                </div>
                <div className="l-how-step-title">{s.title}</div>
                <p className="l-how-step-body">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO VIDEO */}
      <section
        className="l-section"
        id="demo"
        style={{ background: "linear-gradient(180deg,var(--bg1),var(--bg0))" }}
      >
        <div className="l-sec-inner">
          <div className="l-sec-label l-au1">See It In Action</div>
          <h2 className="l-sec-title l-au2">
            Watch ChopTime AI work
            <br />
            on the streets of Accra
          </h2>
          <p className="l-sec-sub l-au3">
            A 60-second demo — voice meal planning, nearby food finding, and a
            family alert being sent live.
          </p>
          <div className="l-video-wrap">
            <div className="l-video-container">
              {videoPlaying && VIDEO_ID !== "YOUR_VIDEO_ID" ? (
                <iframe
                  src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0`}
                  title="ChopTime AI Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div
                  className="l-video-placeholder"
                  onClick={() => {
                    if (VIDEO_ID !== "YOUR_VIDEO_ID") {
                      setVideoPlaying(true);
                      trackEvent("video_play", "engagement", "demo");
                    } else
                      window.open(
                        "https://www.youtube.com/@TheDiabetesVoice",
                        "_blank"
                      );
                  }}
                >
                  <div className="l-video-play-btn">
                    <svg viewBox="0 0 24 24">
                      <path d="M5 3l14 9-14 9z" />
                    </svg>
                  </div>
                  <div className="l-video-title">
                    ChopTime AI — 60 Second Demo
                  </div>
                  <div className="l-video-sub">
                    Voice meal planning · Nearby food · Family alert
                  </div>
                  <div className="l-video-badge">
                    ▶ The Diabetes Voice on YouTube
                  </div>
                  {VIDEO_ID === "YOUR_VIDEO_ID" && (
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text3)",
                        marginTop: 14,
                        padding: "0 20px",
                        textAlign: "center",
                      }}
                    >
                      📹 Upload your demo to YouTube and replace VIDEO_ID in
                      Landing.js to embed it here
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 18 }}>
            <a
              className="l-btn l-btn-ghost"
              href="https://www.youtube.com/@TheDiabetesVoice"
              target="_blank"
              rel="noreferrer"
            >
              ▶ Watch All Episodes on YouTube →
            </a>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section className="l-waitlist" id="waitlist">
        <div className="l-waitlist-inner">
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div className="l-sec-label l-au1">Join the Waitlist</div>
            <h2 className="l-sec-title l-au2" style={{ marginBottom: 10 }}>
              Be the first to use
              <br />
              ChopTime AI in your city
            </h2>
            <p className="l-sec-sub l-au3" style={{ marginBottom: 0 }}>
              247 people have already joined. We're launching city by city —
              starting with Accra, then Lagos, then Dakar.
            </p>
          </div>
          <div className="l-form-card">
            <WaitlistForm />
          </div>
        </div>
      </section>

      {/* INVESTOR SECTION */}
      <section className="l-investor" id="investors">
        <div className="l-investor-inner">
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div className="l-inv-label">For Investors</div>
            <h2 className="l-sec-title" style={{ marginBottom: 10 }}>
              The opportunity to save
              <br />
              <span style={{ color: "var(--amber)" }}>25 million lives</span>
            </h2>
            <p className="l-sec-sub" style={{ marginBottom: 0 }}>
              West Africa has 25M+ diagnosed diabetics. No culturally relevant
              digital health solution exists for them yet. ChopTime AI is first.
            </p>
          </div>
          <div className="l-investor-grid">
            <div>
              <div
                style={{
                  fontFamily: "Syne,sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  marginBottom: 14,
                }}
              >
                📊 Current Traction
              </div>
              <div className="l-traction-grid">
                {[
                  { val: "1,247+", lbl: "Community Members" },
                  { val: "5", lbl: "Countries Active" },
                  { val: "7.4k", lbl: "Best Episode Views" },
                  { val: "247+", lbl: "Waitlist Signups" },
                ].map((t, i) => (
                  <div className="l-traction-card" key={i}>
                    <div className="l-traction-val">{t.val}</div>
                    <div className="l-traction-lbl">{t.lbl}</div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  fontFamily: "Syne,sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  margin: "20px 0 14px",
                }}
              >
                🌍 Market Opportunity
              </div>
              {[
                {
                  icon: "👥",
                  title: "25M+ diabetics in West Africa",
                  body: "Nigeria alone has 11M+. Ghana 1.2M+. Growing 5% annually due to urbanisation and changing diets. Largely unserved by digital health tools.",
                },
                {
                  icon: "📱",
                  title: "High mobile penetration",
                  body: "85%+ smartphone usage in target cities. Users already rely on phones for health decisions, banking, and food — ChopTime AI fits naturally into their lives.",
                },
                {
                  icon: "🏥",
                  title: "Broken healthcare access",
                  body: "Average wait to see a diabetes specialist in Accra: 6 weeks. In Lagos: longer. ChopTime AI provides daily life-saving guidance in that gap.",
                },
                {
                  icon: "💡",
                  title: "Zero direct competition",
                  body: "No app currently combines GI-based West African food guidance, live traffic meal planning, and family emergency alerts in one place. We are first.",
                },
              ].map((m, i) => (
                <div className="l-market-stat" key={i}>
                  <div className="l-market-icon">{m.icon}</div>
                  <div>
                    <div className="l-market-title">{m.title}</div>
                    <p className="l-market-body">{m.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div>
              {/* THE ASK */}
              <div className="l-ask-card" style={{ marginBottom: 14 }}>
                <div className="l-ask-title">💰 The Investment Ask</div>
                {[
                  { lbl: "Stage", val: "Pre-seed · Live MVP" },
                  { lbl: "Raising", val: "$25,000 – $50,000" },
                  { lbl: "Equity offered", val: "5% – 10%" },
                  { lbl: "Pre-money val.", val: "$300,000" },
                  { lbl: "Use of funds", val: "Backend · Flutter app · Team" },
                  { lbl: "Monetisation", val: "Freemium · ₵25/month Premium" },
                ].map((r, i) => (
                  <div className="l-ask-row" key={i}>
                    <span className="l-ask-lbl">{r.lbl}</span>
                    <span className="l-ask-val">{r.val}</span>
                  </div>
                ))}
              </div>

              {/* 12-MONTH PROJECTIONS */}
              <div
                style={{
                  background: "linear-gradient(145deg,#071a13,#0a2820)",
                  border: "1px solid rgba(0,212,160,.2)",
                  borderRadius: "var(--r-md)",
                  padding: 18,
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    fontFamily: "Syne,sans-serif",
                    fontSize: 13,
                    fontWeight: 800,
                    color: "var(--teal)",
                    marginBottom: 12,
                  }}
                >
                  📈 12-Month Projections
                </div>
                {[
                  { lbl: "Active users", val: "50,000+" },
                  { lbl: "Paying Premium", val: "5,000+ users" },
                  { lbl: "Target MRR", val: "₵125,000/month" },
                  { lbl: "Target ARR", val: "₵1,500,000/year" },
                  { lbl: "Countries live", val: "GH · NG · SN · CI · CM" },
                  { lbl: "MEST application", val: "2026 / 2027 cycle" },
                ].map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 0",
                      borderBottom: "1px solid rgba(0,212,160,.1)",
                    }}
                  >
                    <span style={{ fontSize: 12, color: "var(--text2)" }}>
                      {r.lbl}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--teal)",
                      }}
                    >
                      {r.val}
                    </span>
                  </div>
                ))}
              </div>

              {/* FUND ALLOCATION */}
              <div
                style={{
                  background: "var(--bg2)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--r-md)",
                  padding: 16,
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    fontFamily: "Syne,sans-serif",
                    fontSize: 13,
                    fontWeight: 800,
                    marginBottom: 12,
                  }}
                >
                  🏗️ How Funds Will Be Used
                </div>
                {[
                  {
                    icon: "⚙️",
                    lbl: "Backend & database",
                    pct: "35%",
                    note: "Firebase · Node.js · real user auth",
                  },
                  {
                    icon: "📱",
                    lbl: "Flutter mobile app",
                    pct: "35%",
                    note: "Android & iOS · app store ready",
                  },
                  {
                    icon: "👥",
                    lbl: "Development team",
                    pct: "20%",
                    note: "1–2 developers · 6 months",
                  },
                  {
                    icon: "📡",
                    lbl: "APIs & infrastructure",
                    pct: "10%",
                    note: "Google Maps · AfricasTalking SMS",
                  },
                ].map((f, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 10,
                    }}
                  >
                    <div style={{ fontSize: 18, flexShrink: 0 }}>{f.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 3,
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 600 }}>
                          {f.lbl}
                        </span>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            color: "var(--amber)",
                          }}
                        >
                          {f.pct}
                        </span>
                      </div>
                      <div
                        style={{
                          height: 4,
                          background: "var(--bg4)",
                          borderRadius: 2,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: f.pct,
                            background:
                              "linear-gradient(90deg,var(--teal2),var(--teal))",
                            borderRadius: 2,
                          }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "var(--text3)",
                          marginTop: 2,
                        }}
                      >
                        {f.note}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CONTACT */}
              <div className="l-contact-card">
                <div className="l-contact-av">RL</div>
                <div>
                  <div className="l-contact-name">Dr. Richard LAAK</div>
                  <div className="l-contact-role">
                    Founder · Diabetes Health Advocate · Accra, Ghana
                  </div>
                  <div className="l-contact-links">
                    <a
                      className="l-contact-link"
                      href="https://www.youtube.com/@TheDiabetesVoice"
                      target="_blank"
                      rel="noreferrer"
                    >
                      ▶ YouTube
                    </a>
                    <a
                      className="l-contact-link"
                      href="https://t.me/TheDiabetesVoice"
                      target="_blank"
                      rel="noreferrer"
                    >
                      📲 Telegram
                    </a>
                    <a
                      className="l-contact-link"
                      href="mailto:hello@choptime.ai"
                    >
                      ✉️ Email
                    </a>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: 14,
                  padding: "14px 16px",
                  background: "rgba(0,212,160,.05)",
                  border: "1px solid rgba(0,212,160,.12)",
                  borderRadius: "var(--r-md)",
                  fontSize: 12,
                  color: "var(--text2)",
                  lineHeight: 1.75,
                  fontStyle: "italic",
                }}
              >
                "Every family in West Africa deserves the notification I never
                received for Mina. This is not just a business — it is a promise
                to save the lives that generic apps have ignored."
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--teal)",
                    fontWeight: 700,
                    marginTop: 6,
                    fontStyle: "normal",
                  }}
                >
                  — Dr. Richard LAAK · MGL2390
                </div>
              </div>

              <button
                className="l-btn l-btn-amber"
                style={{
                  width: "100%",
                  marginTop: 14,
                  padding: "14px",
                  fontSize: 14,
                  justifyContent: "center",
                  borderRadius: "var(--r-sm)",
                }}
                onClick={() => {
                  scrollTo("waitlist");
                  trackEvent("investor_cta", "engagement", "contact");
                }}
              >
                Request Investment Deck →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="l-cta">
        <div className="l-cta-glow" />
        <div className="l-sec-label l-au1" style={{ marginBottom: 16 }}>
          Join ChopTime AI
        </div>
        <h2 className="l-cta-title l-au2">
          One notification
          <br />
          can save a life.
        </h2>
        <p className="l-cta-sub l-au3">
          Don't let a preventable moment take someone you love. Join 1,247+
          members across West Africa managing diabetes smarter, every single
          day.
        </p>
        <div
          className="l-au4"
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <button
            className="l-btn l-btn-teal l-btn-xl"
            onClick={() => openAuth("signup")}
          >
            Get Started — It's Free
          </button>
          <button
            className="l-btn l-btn-ghost l-btn-lg"
            onClick={() => openAuth("login")}
          >
            Sign In
          </button>
        </div>
        <div
          className="l-au5"
          style={{ display: "flex", justifyContent: "center", marginTop: 24 }}
        >
          <div className="l-wave">
            {[1, 2, 3, 4, 5].map((i) => (
              <span key={i} />
            ))}
          </div>
        </div>
        <div className="l-cta-mgl l-au6">
          IN MEMORY OF MINA GBEMISOLA LAAK · MGL2390 · OCT 23, 1990
        </div>
      </section>

      {/* FOOTER */}
      <footer className="l-footer">
        <div>
          <div className="l-footer-copy">
            © 2025 ChopTime AI · Built with{" "}
            <span className="l-footer-heart">♥</span> by Dr. Richard LAAK
          </div>
          <div className="l-footer-mgl">
            IN MEMORY OF MINA GBEMISOLA LAAK · MGL2390
          </div>
        </div>
        <div className="l-footer-links">
          {["Privacy Policy", "Terms", "Contact", "YouTube", "Telegram"].map(
            (l) => (
              <span className="l-footer-link" key={l}>
                {l}
              </span>
            )
          )}
        </div>
      </footer>

      {/* AUTH MODAL */}
      {authOpen && (
        <AuthModal
          tab={authTab}
          onClose={() => setAuthOpen(false)}
          onSuccess={() => {
            setAuthOpen(false);
            onEnterApp();
          }}
        />
      )}
    </>
  );
}
